/**
 * RegistreUniqueDuPersonnel — Registre légal (art. L1221-13 C. trav.)
 *
 * Compile automatiquement le registre depuis les fiches salariés + contrats,
 * signale les lignes non conformes (champs manquants) et permet l'export PDF
 * A3 paysage + CSV Excel + création de snapshots datés (traçabilité inspection
 * travail / URSSAF).
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BookOpen, Download, FileDown, Camera, ShieldCheck, AlertTriangle,
  Users, UserCheck, UserX, Info, Loader2, ExternalLink, RefreshCw, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtDateFR = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("fr-FR"); }
  catch { return v; }
};

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  terminated: "bg-slate-200 text-slate-700 border-slate-300",
  on_leave: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function RegistreUniqueDuPersonnel() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [register, setRegister] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [reg, snap] = await Promise.all([
        api.get("/hr/rup"),
        api.get("/hr/rup/snapshots"),
      ]);
      setRegister(reg.data);
      setSnapshots(snap.data.snapshots || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await api.get(`/hr/rup/export.${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `RUP_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(null); }
  };

  const createSnapshot = async () => {
    setSnapshotting(true);
    try {
      const r = await api.post("/hr/rup/snapshot");
      toast.success(`Snapshot archivé (${r.data.total_employees} salariés)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSnapshotting(false); }
  };

  const nonCompliantIds = useMemo(() => {
    const map = {};
    (register?.compliance?.missing_fields || []).forEach(w => {
      map[w.employee_id] = w.missing_fields;
    });
    return map;
  }, [register]);

  if (loading) {
    return (
      <div className="p-6 space-y-3" data-testid="rup-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-slate-100 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rup-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <BookOpen className="w-6 h-6 text-slate-700" />
            Registre Unique du Personnel
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Obligation légale · art. L1221-13 C. trav. · Généré automatiquement
            depuis {register?.total || 0} fiche{register?.total > 1 ? "s" : ""} salarié
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load}
            data-testid="rup-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Actualiser
          </Button>
          <Button variant="outline" onClick={() => exportFile("csv")}
            disabled={exporting !== null}
            data-testid="rup-export-csv">
            <FileDown className="w-4 h-4 mr-1.5" />
            {exporting === "csv" ? "…" : "Export CSV"}
          </Button>
          <Button onClick={() => exportFile("pdf")}
            disabled={exporting !== null}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="rup-export-pdf">
            {exporting === "pdf"
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Download className="w-4 h-4 mr-1.5" />}
            Export PDF
          </Button>
          {canWrite && (
            <Button onClick={createSnapshot} disabled={snapshotting}
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="rup-snapshot">
              {snapshotting
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Camera className="w-4 h-4 mr-1.5" />}
              Archiver un snapshot
            </Button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KpiCard label="Total inscrits" value={register?.total || 0}
          icon={Users} color="slate" />
        <KpiCard label="Actifs" value={register?.actifs || 0}
          icon={UserCheck} color="emerald" />
        <KpiCard label="Sortis" value={register?.sortis || 0}
          icon={UserX} color="slate" />
        <KpiCard
          label={register?.compliance?.non_compliant_count > 0
            ? "Fiches à compléter" : "Toutes conformes"}
          value={`${register?.compliance?.compliant_count || 0}/${register?.total || 0}`}
          icon={register?.compliance?.non_compliant_count > 0 ? AlertTriangle : ShieldCheck}
          color={register?.compliance?.non_compliant_count > 0 ? "amber" : "emerald"}
        />
      </div>

      {/* Compliance banner */}
      {register?.compliance?.non_compliant_count > 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <AlertDescription className="text-xs text-amber-900">
            <strong>{register.compliance.non_compliant_count} fiche(s) incomplète(s)</strong> vis-à-vis
            de l'art. D1221-23 C. trav. Complète les champs manquants avant
            un contrôle inspection travail (amende jusqu'à 3 750€ / salarié omis).
          </AlertDescription>
        </Alert>
      )}

      {/* Info légal */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-xs text-blue-900">
          Le registre est <strong>ordonné chronologiquement</strong> par date d'entrée.
          Il doit être consultable immédiatement par l'inspection du travail, l'URSSAF,
          l'IRCANTEC et Pôle emploi. Conservation obligatoire <strong>5 ans</strong> après
          la sortie du salarié. Chaque export PDF archivé constitue une preuve à date.
        </AlertDescription>
      </Alert>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {(register?.rows || []).length === 0 ? (
            <div className="p-10 text-center">
              <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">
                Aucun salarié enregistré. Ajoutez-en depuis la page{" "}
                <button className="text-blue-600 underline"
                  onClick={() => navigate("/app/hr/salaries")}>Salariés</button>.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500 w-12">N°</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Salarié</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Nationalité</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Né(e) le</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Emploi</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Type contrat</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Entrée</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Sortie</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Statut</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 w-24">Conformité</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(register?.rows || []).map((r, i) => {
                  const missing = nonCompliantIds[r.employee_id];
                  return (
                    <motion.tr
                      key={r.employee_id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i, 15) * 0.02, duration: 0.2 }}
                      className={cn(
                        "border-b hover:bg-slate-50 cursor-pointer",
                        missing && "bg-amber-50/40",
                      )}
                      onClick={() => navigate(`/app/hr/employees/${r.employee_id}`)}
                      data-testid={`rup-row-${i}`}
                    >
                      <TableCell className="font-mono text-xs">{r.order_number}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900">
                          {r.last_name} {r.first_name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {r.sex !== "—" && <span className="mr-2">{r.sex}</span>}
                          {r.qualification && r.qualification !== "—" && (
                            <span>{r.qualification}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{r.nationality || "—"}</TableCell>
                      <TableCell className="text-xs">{fmtDateFR(r.birth_date)}</TableCell>
                      <TableCell className="text-xs">{r.job_title || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          "text-[11px]",
                          r.contract_type_code === "cdi"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : r.contract_type_code === "cdd"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-blue-50 text-blue-700 border-blue-200",
                        )}>
                          {r.contract_type}
                        </Badge>
                        {r.contract_end_date && (
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            Fin : {fmtDateFR(r.contract_end_date)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{fmtDateFR(r.hire_date)}</TableCell>
                      <TableCell className="text-xs">
                        {fmtDateFR(r.termination_date)}
                        {r.contract_end_reason && (
                          <div className="text-[10px] text-slate-500">{r.contract_end_reason}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline"
                          className={STATUS_BADGE[r.status] || STATUS_BADGE.active}>
                          {r.status === "active" ? "Actif"
                            : r.status === "terminated" ? "Sorti"
                              : r.status === "on_leave" ? "En congé" : r.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {missing ? (
                          <div className="flex items-center gap-1 text-amber-700"
                                title={`Manque : ${missing.join(', ')}`}>
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span className="text-[11px]">{missing.length}</span>
                          </div>
                        ) : (
                          <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        )}
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Warnings détaillés */}
      {register?.compliance?.missing_fields?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-amber-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Fiches à compléter ({register.compliance.non_compliant_count})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-amber-100">
              {register.compliance.missing_fields.map(w => (
                <div key={w.employee_id}
                      className="p-3 hover:bg-amber-100/40 cursor-pointer flex items-center justify-between"
                      onClick={() => navigate(`/app/hr/employees/${w.employee_id}`)}>
                  <div>
                    <div className="font-medium text-sm text-slate-900">
                      #{w.order_number} · {w.name}
                    </div>
                    <div className="text-xs text-amber-900 mt-0.5">
                      Manque : {w.missing_fields.join(", ")}
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Snapshots history */}
      {snapshots.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="w-4 h-4 text-purple-600" />
              Historique des snapshots ({snapshots.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {snapshots.slice(0, 10).map(s => (
                <div key={s.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {s.filename}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {new Date(s.created_at).toLocaleString("fr-FR")} · {s.created_by_email}
                      {" · "}{s.total_employees} salariés
                      {s.non_compliant_count > 0 && (
                        <span className="text-amber-700 ml-1">
                          · {s.non_compliant_count} incomplet(s)
                        </span>
                      )}
                    </div>
                  </div>
                  <a href={`/api/hr/rup/snapshots/${s.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      className="ml-2 p-2 rounded hover:bg-slate-100">
                    <Download className="w-4 h-4 text-slate-600" />
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  const cls = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  }[color] || "bg-slate-50 text-slate-700";
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-2 border", cls)}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-2xl font-bold text-slate-900">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}
